import { useCallback, useEffect, useState } from "react";

import { useValidateCustomFormLazyQuery } from "@/apollo/types";
import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import { Button } from "@/components/elements/Button";
import Select from "@/components/elements/Select";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { XIcon } from "lucide-react";

import {
  FACEBOOK_ADS_DAILY_AGGREGATION,
  FacebookAdsReport,
  FacebookAdsReportOption,
  isFacebookAdsReportType,
} from "./FacebookAdsForm";

const INVALID_REPORT_MESSAGE = "Invalid report";

export default function FacebookAdsCustomReport(props: {
  onChangeReport: (report: FacebookAdsReport) => void;
  onRemoveReport: (report: FacebookAdsReport) => void;
  onReportInvalid: (reportId: string) => void;
  onReportValid: (reportId: string) => void;
  report: FacebookAdsReport;
  initialBreakdownOptions: FacebookAdsReportOption[];
  initialActionBreakdownOptions: FacebookAdsReportOption[];
  initialFieldOptions: FacebookAdsReportOption[];
  initialLevelOptions: FacebookAdsReportOption[];
  initialActionReportTimeOptions: FacebookAdsReportOption[];
  initialClickOptions: FacebookAdsReportOption[];
  initialAggregationOptions: FacebookAdsReportOption[];
  initialRollingWindowOptions: FacebookAdsReportOption[];
  initialViewOptions: FacebookAdsReportOption[];
  connectionId: string;
  adAccounts?: FacebookAdsReportOption[];
  validationError?: string;
}) {
  const {
    report,
    initialBreakdownOptions,
    initialActionBreakdownOptions,
    initialFieldOptions,
    initialLevelOptions,
    initialActionReportTimeOptions,
    initialClickOptions,
    initialAggregationOptions,
    initialRollingWindowOptions,
    initialViewOptions,
    onChangeReport,
    onRemoveReport,
    validationError,
    connectionId,
    adAccounts,
    onReportValid,
    onReportInvalid,
  } = props;

  const [validActionbreakdowns, setValidActionBreakdowns] = useState<
    FacebookAdsReportOption[] | null
  >(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validBreakdowns, setValidBreakdowns] = useState<
    FacebookAdsReportOption[] | null
  >(null);
  const [validFields, setValidFields] = useState<
    FacebookAdsReportOption[] | null
  >(null);
  const [validLevels, setValidLevels] = useState<
    FacebookAdsReportOption[] | null
  >(null);
  const [validActionReportTime, setValidActionReportTime] = useState<
    FacebookAdsReportOption[] | null
  >(null);
  const [validationCallError, setValidationCallError] = useState<
    boolean | string
  >(false);

  const [callValidationQuery, { loading: validationQueryLoading }] =
    useValidateCustomFormLazyQuery();

  const runValidation = useCallback(
    async (validateViaApi: boolean = false) => {
      //Call validation endpoint
      const queryData = {
        action_breakdowns: report.action_breakdowns.map((x) => x.value),
        breakdowns: report.breakdowns.map((x) => x.value),
        fields: report.fields.map((x) => x.value),
        levels: report.levels,
        click_window: report.click_window.map((x) => x.value),
        view_window: report.view_window.map((x) => x.value),
        action_report_time: report.action_report_time,
        aggregation: report.aggregation,
        rolling_window: report.rolling_window,
        id: report.id,
      };
      const { data, error } = await callValidationQuery({
        variables: {
          input: {
            connectionId,
            payload: JSON.stringify({
              report: queryData,
              adAccounts,
              validateViaApi,
            }),
          },
        },
      });
      //If error occurs, show warning and use initial metrics/dimensions
      if (error) {
        if (error.message === INVALID_REPORT_MESSAGE) {
          onReportInvalid(report.id);
        } else {
          setValidationCallError(true);
        }
        setValidActionBreakdowns(null);
        setValidBreakdowns(null);
        setValidFields(null);
        setValidLevels(null);
        setValidActionReportTime(null);
      }

      const result = data?.validateCustomForm.customFormValidationResult;
      if (result) {
        setValidActionBreakdowns(
          result.report.action_breakdowns.map((x: string) => ({
            label: x,
            value: x,
          })),
        );
        setValidBreakdowns(
          result.report.breakdowns.map((x: string) => ({
            label: x,
            value: x,
          })),
        );
        if (!result.apiError) {
          if (result.apiValidated) {
            setValidationSuccess(true);
            onReportValid(report.id);
            setValidationCallError(false);
          } else if (validateViaApi) {
            // Without an explicit validation request, the backend never calls the Facebook
            // API, so apiValidated is always false here. Only treat that as a real failure
            // when the user asked for API validation — otherwise every already-saved report
            // would be marked invalid on mount and block custom-reports from ever committing.
            setValidationCallError(true);
            onReportInvalid(report.id);
          }
        } else if (validateViaApi) {
          setValidationCallError(result.apiErrorMessage);
          setValidationSuccess(false);
          onReportInvalid(report.id);
        }
      }
    },
    [
      adAccounts,
      callValidationQuery,
      connectionId,
      onReportInvalid,
      onReportValid,
      report.action_breakdowns,
      report.breakdowns,
      report.fields,
      report.action_report_time,
      report.id,
      report.levels,
      report.click_window,
      report.view_window,
      report.aggregation,
      report.rolling_window,
    ],
  );

  useEffect(() => {
    if (report.breakdowns.length > 0 || report.action_breakdowns.length > 0) {
      runValidation();
    } else {
      setValidActionBreakdowns(null);
      setValidBreakdowns(null);
      onReportValid(report.id); //If we cannot validate, we assume it is valid
    }
  }, [
    onReportValid,
    report.action_breakdowns.length,
    report.breakdowns.length,
    report.id,
    runValidation,
  ]);

  const changeReport = (report: FacebookAdsReport) => {
    onChangeReport(report);
    setValidationSuccess(false);
  };

  const isRolling = !!report.rolling_window;

  return (
    <div
      className="relative rounded-xs border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700"
      key={report.id}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel id="name" required={true}>
            Name
          </FieldLabel>
          <Input
            value={report.name}
            onChange={(e) => changeReport({ ...report, name: e.target.value })}
          />
          {isFacebookAdsReportType(report.name) && (
            <div className="text-red-500">
              {report.name} is a reserved report name and cannot be used.
            </div>
          )}
          <InputDescription className="m-1">
            Converted to snake_case for the stream and destination table, so the
            table name may differ slightly from what you type here — a digit
            next to a letter becomes its own word, making "rolling_reach_7d"
            into "rolling_reach_7_d".
          </InputDescription>
        </div>
        {/*This form element does not have any effect, having this is handled in the backend. The field is needed for UX understandability*/}
        <div>
          <FieldLabel id="date" required={true}>
            Required dimensions
          </FieldLabel>
          <div className="dark:bg-gray-000 rounded-xs border bg-gray-100 p-2 opacity-75 dark:border-gray-700 dark:bg-gray-800">
            <span className="rounded-xs bg-black px-2 py-1 text-xs text-white dark:bg-gray-700">
              date
            </span>
          </div>
        </div>
        <div>
          <FieldLabel htmlFor={`breakdown-${report.id}`} required={true}>
            Action breakdown ({report.action_breakdowns?.length ?? 0}/
            {(validActionbreakdowns || initialActionBreakdownOptions)?.length ??
              0}
            )
          </FieldLabel>
          <Select
            id={`breakdown-${report.id}`}
            isMulti={true}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption[]) =>
              changeReport({ ...report, action_breakdowns: v })
            }
            // isOptionDisabled={() => report.dimensions?.length >= 8}
            options={validActionbreakdowns || initialActionBreakdownOptions}
            value={report.action_breakdowns}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`breakdown-${report.id}`} required={true}>
            Breakdowns ({report.breakdowns?.length ?? 0}/
            {(validBreakdowns || initialBreakdownOptions)?.length ?? 0})
          </FieldLabel>
          <Select
            id={`breakdown-${report.id}`}
            isMulti={true}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption[]) =>
              changeReport({ ...report, breakdowns: v })
            }
            options={validBreakdowns || initialBreakdownOptions}
            // isOptionDisabled={() => report.metrics?.length >= 10}
            value={report.breakdowns}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`fields-${report.id}`} required={true}>
            Fields ({report.fields?.length ?? 0}/
            {initialFieldOptions?.length ?? 0})
          </FieldLabel>
          <Select
            id={`fields-${report.id}`}
            isMulti={true}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption[]) =>
              changeReport({ ...report, fields: v })
            }
            options={validFields || initialFieldOptions}
            value={report.fields}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`level-${report.id}`} required={true}>
            Level
          </FieldLabel>
          <Select
            id={`level-${report.id}`}
            isMulti={false}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption) => {
              changeReport({ ...report, levels: v });
            }}
            options={validLevels || initialLevelOptions}
            value={report.levels}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`actionReportTime-${report.id}`} required={true}>
            Action Report Time
          </FieldLabel>
          <Select
            id={`actionReportTime-${report.id}`}
            isMulti={false}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption) =>
              changeReport({ ...report, action_report_time: v })
            }
            options={validActionReportTime || initialActionReportTimeOptions}
            value={report.action_report_time}
          />
        </div>
        <div>
          <FieldLabel
            htmlFor={`windowAttribution-${report.id}`}
            required={true}
          >
            View Window Attribution
          </FieldLabel>
          <Select
            id={`windowAttribution-${report.id}`}
            isMulti={true}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption[]) =>
              changeReport({ ...report, view_window: v })
            }
            options={initialViewOptions}
            value={report.view_window}
          />
        </div>
        <div>
          <FieldLabel id={`clickAttribution-${report.id}`} required={true}>
            Click Window Attribution
          </FieldLabel>
          <Select
            id={`clickAttribution-${report.id}`}
            isMulti={true}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            onChange={(v: FacebookAdsReportOption[]) =>
              changeReport({ ...report, click_window: v })
            }
            options={initialClickOptions}
            value={report.click_window}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`aggregation-${report.id}`} required={true}>
            Aggregation
          </FieldLabel>
          <Select
            id={`aggregation-${report.id}`}
            isMulti={false}
            isLoading={validationQueryLoading}
            placeholder="Select one"
            disabled={isRolling}
            isClearable={false}
            onChange={(v: FacebookAdsReportOption) =>
              changeReport({ ...report, aggregation: v })
            }
            options={initialAggregationOptions}
            value={report.aggregation}
          />
          <InputDescription className="m-1">
            {isRolling
              ? "Not applicable while a rolling window is set — Facebook ignores the aggregation when a rolling window is requested."
              : "Calendar-aligned buckets: weeks start on Monday, months on the 1st."}
          </InputDescription>
        </div>
        {initialRollingWindowOptions.length > 0 && (
          <div>
            <FieldLabel htmlFor={`rollingWindow-${report.id}`} required={false}>
              Rolling window
            </FieldLabel>
            <Select
              id={`rollingWindow-${report.id}`}
              isMulti={false}
              isLoading={validationQueryLoading}
              placeholder="None — use aggregation instead"
              isClearable={true}
              onChange={(v: FacebookAdsReportOption | null) =>
                changeReport({
                  ...report,
                  rolling_window: v ?? undefined,
                  // Aggregation and rolling window are mutually exclusive, and the backend
                  // rejects the combination outright. Forcing daily here keeps the report
                  // valid without making the user clear the other field first.
                  aggregation: v
                    ? FACEBOOK_ADS_DAILY_AGGREGATION
                    : report.aggregation,
                })
              }
              options={initialRollingWindowOptions}
              value={report.rolling_window ?? null}
            />
            <InputDescription className="m-1">
              A window that slides with each report date, e.g. "Last 7 days"
              gives one row per day covering that day and the six before it, and
              "Last 12 months" gives one row per calendar month covering that
              month and the eleven before it. Needed for deduplicated reach and
              frequency, which Facebook only deduplicates within the window it
              is asked for — summing daily rows inflates reach and understates
              frequency. Day windows are keyed on the window's end date, month
              windows on its start date, since the running month's end date
              moves until the month is over.
            </InputDescription>
          </div>
        )}
      </div>
      <div className="absolute top-0 right-0 p-2">
        <button type="button" onClick={() => onRemoveReport(report)}>
          <XIcon className="size-4" />
        </button>
      </div>
      <div className="mt-4">
        <div>
          <Button
            variant="solid"
            colorScheme="primary"
            onClick={() => runValidation(true)}
          >
            Validate report
          </Button>
        </div>

        {validationSuccess ? (
          <div className="mt-2">
            <Alert status="success">
              <AlertIcon />
              Report is valid
            </Alert>
          </div>
        ) : null}
        {validationError ? (
          <div className="mt-2">
            <Alert status="error">
              <AlertIcon />
              {validationError}
            </Alert>
          </div>
        ) : null}
        {validationCallError === true && validationSuccess !== true ? (
          <div className="mt-2">
            <Alert status="info">
              <AlertIcon />
              Validate your report to continue
            </Alert>
          </div>
        ) : null}
        {validationCallError !== true && validationCallError ? (
          <div className="mt-2">
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Facebook API error:</AlertTitle>
              {validationCallError}
            </Alert>
          </div>
        ) : null}
      </div>
    </div>
  );
}
